import React, { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import AdminOrdersService from "../../../Modules/Services/admin.orders.service";
import { AdminDrawer } from "../../../Components/Admin/AdminOverlays";

const formatRub = (value) =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const toDateAssumingYakutsk = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const s = value.trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?$/);
    if (m) return new Date(`${m[1]}T${m[2]}${m[3] || ""}+09:00`);
    return new Date(s);
  }
  return new Date(value);
};

const formatDateTime = (v) => {
  try {
    const d = toDateAssumingYakutsk(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat("ru-RU", {
      timeZone: "Asia/Yakutsk",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return String(v);
  }
};

const ORDER_STATUS_META = {
  new: { label: "Новый", cls: "admin-tag--accent" },
  preparing: { label: "Готовится", cls: "admin-tag--warn" },
  ready: { label: "Готов", cls: "admin-tag--success" },
  completed: { label: "Выдан", cls: "admin-tag--muted" },
};

const statusBadge = (status) => {
  const meta = ORDER_STATUS_META[status] || {
    label: String(status || "—"),
    cls: "admin-tag--muted",
  };
  return (
    <span className={`admin-tag ${meta.cls}`}>{meta.label}</span>
  );
};

const ManageOrders = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await AdminOrdersService.getOrders(page, pageSize);
      if (res?.success) {
        setRows(res.data.items || []);
        setTotal(Number(res.data.total || 0));
      }
    } catch (e) {
      console.error("Failed to fetch orders:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, pageSize]);

  const openOrder = async (id) => {
    try {
      setDrawerOpen(true);
      setSelectedOrder(null);
      setDetailsLoading(true);
      const res = await AdminOrdersService.getOrderById(id);
      if (res?.success) setSelectedOrder(res.data);
    } catch (e) {
      console.error("Failed to fetch order:", e);
    } finally {
      setDetailsLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const copyPhone = (phone, key) => {
    if (!phone) return;
    navigator.clipboard?.writeText(String(phone)).catch(() => {});
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
  };

  return (
    <div>
      <h1 className="admin-page-title">Заказы</h1>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="admin-spinner">Загрузка…</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>№</th>
                <th>Тип</th>
                <th>Телефон</th>
                <th>Адрес</th>
                <th>Товары</th>
                <th>Сумма товаров</th>
                <th>Доставка</th>
                <th>Итого</th>
                <th>Статус</th>
                <th>Создан</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>
                    {r.order_type === "delivery" ? (
                      <span className="admin-tag admin-tag--accent">Доставка</span>
                    ) : (
                      <span className="admin-tag admin-tag--muted">Самовывоз</span>
                    )}
                  </td>
                  <td>
                    <div className="admin-phone-cell">
                      <span className="admin-phone-cell__num">{r.phone}</span>
                      <button
                        type="button"
                        className="admin-copy-btn"
                        title="Копировать номер"
                        aria-label="Копировать номер телефона"
                        onClick={() => copyPhone(r.phone, `row-${r.id}`)}
                      >
                        {copiedKey === `row-${r.id}` ? (
                          <Check size={16} strokeWidth={2.5} aria-hidden />
                        ) : (
                          <Copy size={16} strokeWidth={2} aria-hidden />
                        )}
                      </button>
                    </div>
                  </td>
                  <td style={{ maxWidth: 220 }}>
                    {r.order_type === "delivery" ? r.address || "—" : "—"}
                  </td>
                  <td>{r.total_items}</td>
                  <td>{formatRub(r.total_price)}</td>
                  <td>
                    {r.order_type === "delivery" ? formatRub(r.delivery_fee) : "—"}
                  </td>
                  <td>{formatRub(r.total_sum)}</td>
                  <td>{statusBadge(r.status)}</td>
                  <td>{formatDateTime(r.created_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--primary btn--small"
                      onClick={() => openOrder(r.id)}
                    >
                      Открыть
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="admin-pagination">
        <span>
          Всего: {total} · стр. {page} / {totalPages}
        </span>
        <div className="admin-pagination__controls">
          <button
            type="button"
            className="admin-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Назад
          </button>
          <button
            type="button"
            className="admin-pagination__btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Вперёд
          </button>
          <label>
            На странице{" "}
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <AdminDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedOrder ? `Заказ №${selectedOrder.id}` : "Заказ"}
      >
        {detailsLoading && <div className="admin-spinner">Загрузка…</div>}
        {!detailsLoading && selectedOrder && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <div>
              Тип:{" "}
              <strong>
                {selectedOrder.order_type === "delivery"
                  ? "Доставка"
                  : "Самовывоз"}
              </strong>
            </div>
            <div className="admin-phone-cell admin-phone-cell--block">
              <span>
                Телефон: <strong>{selectedOrder.phone}</strong>
              </span>
              <button
                type="button"
                className="admin-copy-btn"
                title="Копировать номер"
                aria-label="Копировать номер телефона"
                onClick={() => copyPhone(selectedOrder.phone, "drawer")}
              >
                {copiedKey === "drawer" ? (
                  <Check size={16} strokeWidth={2.5} aria-hidden />
                ) : (
                  <Copy size={16} strokeWidth={2} aria-hidden />
                )}
              </button>
            </div>
            {selectedOrder.order_type === "delivery" && (
              <div>Адрес: {selectedOrder.address || "—"}</div>
            )}
            <div>Создан: {formatDateTime(selectedOrder.created_at)}</div>
            <div>Товары: <strong>{formatRub(selectedOrder.total_price)}</strong></div>
            {selectedOrder.order_type === "delivery" && (
              <div>
                Доставка: <strong>{formatRub(selectedOrder.delivery_fee)}</strong>
              </div>
            )}
            <div>
              Итого: <strong>{formatRub(selectedOrder.total_sum)}</strong>
            </div>
            <div>
              Статус: {statusBadge(selectedOrder.status)}
            </div>

            <div className="admin-card" style={{ marginTop: "0.5rem" }}>
              <h3 className="admin-card__title" style={{ fontSize: "1.05rem" }}>
                Состав заказа
              </h3>
              <div className="admin-card__inner">
                {(selectedOrder.items || []).map((it) => {
                  const option =
                    it.size || it.weight ? ` (${it.size || it.weight})` : "";
                  const sum = Number(it.price) * Number(it.quantity);
                  return (
                    <div key={it.id} style={{ marginBottom: "0.85rem" }}>
                      <div style={{ fontWeight: 700 }}>
                        {it.product_name}
                        {option}
                      </div>
                      <div className="admin-muted">
                        {it.quantity} × {formatRub(it.price)} ={" "}
                        <strong>{formatRub(sum)}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </AdminDrawer>
    </div>
  );
};

export default ManageOrders;
